// Pure route/geo helpers shared by driver pages — kept outside React so hooks
// have stable references and the logic is unit-testable.

// Sort index assigned to tickets that didn't make it into the optimized route.
export const UNROUTED_SORT_INDEX = Number.MAX_SAFE_INTEGER;

export const extractZip = (addr) => {
  const m = /\b(\d{5})(?:-\d{4})?\b/.exec(addr || "");
  return m ? m[1] : null;
};

// True when a ticket bills only products — no chlorine delivery.
export const isProductsOnlyTicket = (t) =>
  t.products_only ||
  ((t.pool_deliveries?.length || 0) === 0 && Number(t.gallons || 0) <= 0 && (t.items || []).length > 0);

export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// First geocoded pool coordinate on a ticket — falls back to the customer's
// geocoded address (products-only tickets have no pools) — or null.
export const ticketCoord = (t) => {
  for (const d of t.pool_deliveries || []) {
    if (d.latitude != null && d.longitude != null) return [Number(d.latitude), Number(d.longitude)];
  }
  if (t.customer_latitude != null && t.customer_longitude != null) {
    return [Number(t.customer_latitude), Number(t.customer_longitude)];
  }
  return null;
};

// Distance (miles) from the driver to the nearest pool on a ticket
// (or its customer address when there are no pools), or null.
export const nearestTicketMiles = (driverLoc, t) => {
  if (!driverLoc) return null;
  let coords = (t.pool_deliveries || [])
    .map((d) => (d.latitude != null && d.longitude != null ? [Number(d.latitude), Number(d.longitude)] : null))
    .filter(Boolean);
  if (coords.length === 0) {
    const fallback = ticketCoord(t);
    if (!fallback) return null;
    coords = [fallback];
  }
  return Math.min(...coords.map(([la, ln]) => haversineMiles(driverLoc.lat, driverLoc.lng, la, ln)));
};

/**
 * Nearest-neighbor route from driverLoc through all tickets that have coords.
 * Returns ordered ticket-id array. Tickets without coords are appended at the end.
 */
export const buildRouteOrder = (arr, driverLoc) => {
  if (!driverLoc) return arr.map((t) => t.id);
  const withCoord = arr.filter((t) => ticketCoord(t));
  const without = arr.filter((t) => !ticketCoord(t));
  const remaining = new Set(withCoord.map((t) => t.id));
  const order = [];
  let cur = [driverLoc.lat, driverLoc.lng];
  while (remaining.size) {
    let best = null;
    let bestD = Infinity;
    for (const t of withCoord) {
      if (!remaining.has(t.id)) continue;
      const [la, ln] = ticketCoord(t);
      const d = haversineMiles(cur[0], cur[1], la, ln);
      if (d < bestD) { bestD = d; best = t; }
    }
    if (!best) break;
    order.push(best.id);
    remaining.delete(best.id);
    cur = ticketCoord(best);
  }
  return [...order, ...without.map((t) => t.id)];
};
